﻿import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useAuth } from '../hooks/useAuth'
import {
  RotateCcw,
  ArrowLeft,
  ArrowRight,
  Check,
  X,
  RefreshCw,
  Eye,
  EyeOff,
  Settings,
  BarChart3,
  Clock,
  Target,
  Lightbulb,
  Volume2,
  Pause,
  Play
} from 'lucide-react'
import { RootState } from '../store'
import {
  fetchDeckById,
  startStudySession,
  updateCardProgress,
  completeStudySession
} from '../store/slices/flashcardSlice'
import { flashcardService } from '../services/flashcardService'
import { Flashcard, StudyMode, StudySessionResult } from '../types/flashcard'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import Badge from '../components/ui/Badge'
import LoadingSpinner from '../components/ui/LoadingSpinner'
// Removed unused ProgressBar import
import { useTheme } from '../hooks'

interface FlashcardProps {
  card: Flashcard
  isFlipped: boolean
  onFlip: () => void
  showHint: boolean
  onToggleHint: () => void
}

const FlashcardComponent: React.FC<FlashcardProps> = ({
  card,
  isFlipped,
  onFlip,
  showHint,
  onToggleHint
}) => {
  const [isFlipping, setIsFlipping] = React.useState(false)
  const [displayCard, setDisplayCard] = React.useState(card)
  
  // Update display card only when not flipping to prevent animation interruption
  React.useEffect(() => {
    if (!isFlipping && !isFlipped) {
      setDisplayCard(card)
    }
  }, [card, isFlipping, isFlipped])
  
  const handleFlip = () => {
    if (isFlipping) return // Prevent multiple clicks during animation
    
    setIsFlipping(true)
    onFlip()
    
    // Reset flipping state after animation completes
    setTimeout(() => {
      setIsFlipping(false)
      // Update display card after flip animation completes
      if (isFlipped) {
        setDisplayCard(card)
      }
    }, 700) // Match the CSS transition duration
  }
  
  return (
    <div className="relative w-full min-h-[400px] sm:min-h-[450px] md:h-[500px] perspective-1000">
      <div 
          className={`flashcard-container relative w-full min-h-full md:h-full transform-style-preserve-3d cursor-pointer transition-transform duration-700 ease-in-out ${
            isFlipped ? 'rotate-y-180' : ''
          }`}
          onClick={handleFlip}
        >
        {/* Front Side - Question */}
        <div className="flashcard-side backface-hidden absolute inset-0">
          <Card className="w-full min-h-full md:h-full p-4 sm:p-6 md:p-8 flex flex-col bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-slate-800 dark:via-slate-700 dark:to-slate-600 border-2 border-blue-200 dark:border-slate-600 transition-all duration-300 md:overflow-hidden rounded-2xl">
            <div className="flex-1 flex flex-col justify-center items-center text-center min-h-0 space-y-4">
              <div className="flex-shrink-0">
                <Badge className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg px-3 py-1 text-xs sm:text-sm font-medium rounded-full">
                  ✓ Soru
                </Badge>
              </div>
              
              <div className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 dark:text-gray-100 leading-relaxed md:overflow-y-auto md:max-h-48 px-2">
                {displayCard.question || 'Soru yükleniyor...'}
              </div>
              
              {displayCard.questionImage && (
                <div className="flex-shrink-0 max-w-full">
                  <img 
                    src={displayCard.questionImage} 
                    alt="Soru görseli" 
                    className="max-w-full max-h-32 sm:max-h-40 mx-auto rounded-xl shadow-lg object-contain border border-gray-200 dark:border-gray-600"
                  />
                </div>
              )}
              
              {displayCard.hint && (
                <div className="mt-auto pt-4 w-full">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation()
                      onToggleHint()
                    }}
                    className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white border-0 hover:from-yellow-500 hover:to-orange-600 shadow-md text-xs sm:text-sm font-medium rounded-full px-4 py-2 transition-all duration-200"
                  >
                    <Lightbulb size={16} className="mr-2" />
                    {showHint ? 'İpucunu Gizle' : 'İpucu Göster'}
                  </Button>
                  
                  {showHint && (
                    <div className="mt-3 p-3 sm:p-4 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/30 dark:to-orange-900/30 border border-yellow-200 dark:border-yellow-700 rounded-xl shadow-inner">
                      <p className="text-sm text-yellow-800 dark:text-yellow-200 leading-relaxed">
                        {displayCard.hint}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="mt-4 text-xs sm:text-sm text-gray-500 dark:text-gray-400 flex-shrink-0 text-center font-medium">
              Kartı çevirmek için tıklayın
            </div>
          </Card>
        </div>
        
        {/* Back Side - Answer */}
        <div className="flashcard-side backface-hidden absolute inset-0 rotate-y-180">
          <Card className="w-full min-h-full md:h-full p-4 sm:p-6 md:p-8 flex flex-col bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 dark:from-emerald-900/40 dark:via-green-800/40 dark:to-teal-800/40 border-2 border-green-200 dark:border-emerald-600 transition-all duration-300 md:overflow-hidden rounded-2xl">
            <div className="flex-1 flex flex-col justify-center items-center text-center min-h-0 space-y-4">
              <div className="flex-shrink-0">
                <Badge className="bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg px-3 py-1 text-xs sm:text-sm font-medium rounded-full">
                  Cevap
                </Badge>
              </div>
              
              <div className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 dark:text-gray-100 leading-relaxed md:overflow-y-auto md:max-h-32 px-2">
                {displayCard.answer || 'Cevap yükleniyor...'}
              </div>
              
              {displayCard.answerImage && (
                <div className="flex-shrink-0 max-w-full">
                  <img 
                    src={displayCard.answerImage} 
                    alt="Cevap görseli" 
                    className="max-w-full max-h-24 sm:max-h-32 mx-auto rounded-xl shadow-lg object-contain border border-gray-200 dark:border-gray-600"
                  />
                </div>
              )}
              
              {displayCard.explanation && (
                <div className="flex-1 w-full md:max-h-40 md:overflow-y-auto">
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border border-blue-200 dark:border-blue-700 rounded-xl text-left shadow-inner">
                    <h4 className="font-semibold text-blue-900 dark:text-blue-200 mb-2 text-sm flex items-center">
                      Açıklama:
                    </h4>
                    <p className="text-sm text-blue-800 dark:text-blue-300 leading-relaxed">
                      {displayCard.explanation}
                    </p>
                  </div>
                </div>
              )}
              
              {card.tags && card.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 justify-center flex-shrink-0 max-w-full">
                  {card.tags.map((tag, index) => (
                    <Badge key={index} className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs px-2 py-1 rounded-full shadow-sm">
                      #{tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}

const FlashcardStudy: React.FC = () => {
  const { deckId } = useParams<{ deckId: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { isAuthenticated, loading: authLoading } = useAuth()
  const { sidebarOpen } = useTheme()
  
  const {
    currentDeck,
    currentSession,
    loading,
    error
  } = useSelector((state: RootState) => state.flashcard)
  
  const [currentCardIndex, setCurrentCardIndex] = useState(0)
  const [isFlipped, setIsFlipped] = useState(false)
  const [showHint, setShowHint] = useState(false)
  const [studyMode, setStudyMode] = useState<StudyMode>({
    type: 'normal',
    showHints: true,
    autoFlip: false,
    timeLimit: null
  })
  const [sessionStats, setSessionStats] = useState({
    cardsStudied: 0,
    cardsCorrect: 0,
    cardsIncorrect: 0,
    cardsToReview: 0,
    startTime: new Date()
  })
  const [isSessionActive, setIsSessionActive] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
const [isTransitioning, setIsTransitioning] = useState(false)
  const flashcardRef = useRef<HTMLDivElement | null>(null)
  const didAutoScrollRef = useRef(false)

  // Compute current card values BEFORE any effects that depend on them to avoid TDZ
  const currentCard = currentDeck?.cards[currentCardIndex]
  const totalCards = currentDeck?.cards.length || 0
  const progress = totalCards > 0 ? ((currentCardIndex + 1) / totalCards) * 100 : 0

  useEffect(() => {
    didAutoScrollRef.current = false
  }, [deckId])

  useEffect(() => {
    if (flashcardRef.current && !didAutoScrollRef.current && currentCard) {
      didAutoScrollRef.current = true
      try {
        flashcardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      } catch {}
    }
  }, [currentCard])
  
  
  useEffect(() => {
    const fetchDeckWithErrorHandling = async () => {
      // Auth yüklenmesi tamamlanmadan işlem yapma
      if (authLoading) {
        console.log('FlashcardStudy - Auth still loading, waiting...')
        return
      }
      
      // Kullanıcı giriş yapmamışsa flashcards sayfasına yönlendir
      if (!isAuthenticated) {
        console.log('FlashcardStudy - User not authenticated, redirecting to flashcards')
        navigate('/flashcards')
        return
      }
      
      if (deckId) {
        console.log('FlashcardStudy - deckId from URL:', deckId)
        // Her zaman yeni deste yükle, cache'lenmiş deste farklı olabilir
        if (!currentDeck || currentDeck.id !== deckId) {
          try {
            await dispatch(fetchDeckById(deckId) as any)
          } catch (error) {
            console.error('Critical error in FlashcardStudy page:', error)
            
            // Check for Firestore internal errors
            if (error instanceof Error && (
              error.message.includes('INTERNAL ASSERTION FAILED') ||
              error.message.includes('Unexpected state') ||
              error.message.includes('client has already been terminated')
            )) {
              console.warn('Firestore internal error detected in study page, attempting recovery...')
              
              // Try to navigate back to flashcards page
              try {
                localStorage.clear()
                sessionStorage.clear()
                
                // Navigate back to flashcards page
                navigate('/flashcards')
                
                // Show user-friendly message
                setTimeout(() => {
                  window.location.reload()
                }, 1000)
              } catch (recoveryError) {
                console.error('Failed to recover from error:', recoveryError)
              }
            }
          }
        }
      }
    }
    
    fetchDeckWithErrorHandling()
  }, [deckId, dispatch, currentDeck, navigate, isAuthenticated, authLoading])
  
  useEffect(() => {
    if (currentDeck) {
      console.log('FlashcardStudy - currentDeck loaded:', {
        id: currentDeck.id,
        title: currentDeck.title,
        totalCards: currentDeck.cards.length,
        currentCard: currentDeck.cards[currentCardIndex]
      })
      
      // İlk kartı kontrol et
      if (currentDeck.cards[0]) {
        console.log('FlashcardStudy - First card:', {
          question: currentDeck.cards[0].question,
          answer: currentDeck.cards[0].answer,
          id: currentDeck.cards[0].id
        })
      }
    }
  }, [currentDeck, currentCardIndex])
  
  useEffect(() => {
    if (currentDeck && !isSessionActive) {
      handleStartSession()
    }
  }, [currentDeck, isSessionActive])
  
  // Reset card state when currentCardIndex changes
  useEffect(() => {
    setIsFlipped(false)
    setShowHint(false)
  }, [currentCardIndex])
  
  const handleStartSession = useCallback(async () => {
    if (!currentDeck) return
    
    try {
      await dispatch(startStudySession({ deckId: currentDeck.id, mode: studyMode }) as any)
      setIsSessionActive(true)
      setSessionStats({
        cardsStudied: 0,
        cardsCorrect: 0,
        cardsIncorrect: 0,
        cardsToReview: 0,
        startTime: new Date()
      })
    } catch (error) {
      console.error('Error starting session:', error)
    }
  }, [currentDeck, studyMode, dispatch])
  

 const handleCardResponse = useCallback(async (status: 'correct' | 'incorrect' | 'review') => {
   if (!currentCard || !currentDeck || isTransitioning) return
   
   // Store current card reference before state changes
   const cardToProcess = currentCard
   
   // Update UI stats immediately
   setSessionStats(prev => ({
     ...prev,
     cardsStudied: prev.cardsStudied + 1,
     cardsCorrect: prev.cardsCorrect + (status === 'correct' ? 1 : 0),
     cardsIncorrect: prev.cardsIncorrect + (status === 'incorrect' ? 1 : 0),
     cardsToReview: prev.cardsToReview + (status === 'review' ? 1 : 0)
   }))

   // Start transition: hide answer first, then move to next after animation completes
   setIsTransitioning(true)
   setShowHint(false)
   // Flip animasyon süresi ile eşitle (FlashcardComponent: duration-700)
   const flipDuration = 700
   const proceedToNext = () => {
     if (currentCardIndex < totalCards - 1) {
       setCurrentCardIndex(prev => prev + 1)
     } else {
       handleCompleteSession()
     }
     setIsTransitioning(false)
   }
   if (isFlipped) {
     setIsFlipped(false)
     setTimeout(proceedToNext, flipDuration)
   } else {
     proceedToNext()
   }
   // Background persistence (don't block UI)
   setTimeout(async () => {
     try {
       const promises: Promise<any>[] = []

       if (status === 'incorrect') {
         promises.push(
           flashcardService.saveIncorrectCard(
             cardToProcess.id,
             currentDeck.id,
             currentSession?.id
           )
         )
       } else if (status === 'review') {
         promises.push(
           flashcardService.saveReviewCard(
             cardToProcess.id,
             currentDeck.id,
             currentSession?.id
           )
         )
       }

       if (currentSession) {
         promises.push(
           dispatch(
             updateCardProgress({
               cardId: cardToProcess.id,
               status,
               sessionId: currentSession.id,
               timeSpent: 30
             }) as any
           )
         )
       }
       await Promise.all(promises)
     } catch (error) {
       console.error('Error updating card progress in background:', error)
     }
   }, 0)
 }, [currentCard, currentDeck, currentSession, currentCardIndex, totalCards, dispatch, isFlipped, isTransitioning])
  const handleCompleteSession = async () => {
    try {
      if (currentSession) {
        // Complete the session in Firebase ve sonucu yönlendirmeye state olarak taşı
        const resultAction = await dispatch(completeStudySession(currentSession.id) as any)
        const sessionResult = (resultAction as any)?.payload as StudySessionResult
        navigate(`/flashcards/session-complete/${currentSession.id}`, { state: { sessionResult } })
      } else {
        // If no session, navigate with session stats as state
        const sessionResult = {
          sessionId: 'local-session',
          cardsStudied: sessionStats.cardsStudied,
          cardsCorrect: sessionStats.cardsCorrect,
          cardsIncorrect: sessionStats.cardsIncorrect,
          duration: Math.floor((new Date().getTime() - sessionStats.startTime.getTime()) / 1000),
          score: sessionStats.cardsStudied > 0 ? Math.round((sessionStats.cardsCorrect / sessionStats.cardsStudied) * 100) : 0,
          xpEarned: sessionStats.cardsCorrect * 10,
          badgesEarned: []
        }
        navigate('/flashcards/session-complete/local', { state: { sessionResult } })
      }
    } catch (error) {
      console.error('Error completing session:', error)
    }
  }
  
  const handleNextCard = () => {
    if (isTransitioning) return
    if (currentCardIndex < totalCards - 1) {
      setShowHint(false)
      if (isFlipped) {
        setIsTransitioning(true)
        setIsFlipped(false)
        setTimeout(() => {
          setCurrentCardIndex(prev => prev + 1)
          setIsTransitioning(false)
        }, 700)
      } else {
        setCurrentCardIndex(prev => prev + 1)
      }
    }
  }
  
const handlePrevCard = () => {
  if (isTransitioning) return
  if (currentCardIndex > 0) {
    setShowHint(false)
    if (isFlipped) {
      setIsTransitioning(true)
      setIsFlipped(false)
      setTimeout(() => {
        setCurrentCardIndex(prev => prev - 1)
        setIsTransitioning(false)
      }, 700)
    } else {
      setCurrentCardIndex(prev => prev - 1)
    }
  }
}
  
const handleFlipCard = () => {
   if (isTransitioning) return
   setIsFlipped(!isFlipped)
 }
  
const handleToggleHint = () => {
   if (isTransitioning) return
   setShowHint(!showHint)
 }
  

  
  const getElapsedTime = () => {
    const elapsed = Math.floor((new Date().getTime() - sessionStats.startTime.getTime()) / 1000)
    const minutes = Math.floor(elapsed / 60)
    const seconds = elapsed % 60
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }
  
  if (loading || authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }
  
  if (error || !currentDeck || !currentCard) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {error || 'Deste bulunamadı'}
          </h2>
          <Button onClick={() => navigate('/flashcards')}>
            Flashcard'lara Dön
          </Button>
        </Card>
      </div>
    )
  }
  
  return (
    <div className="min-h-screen bg-transparent overflow-x-hidden">
      {/* Header */}
      <div className="px-4 md:px-8 pt-8 pb-56 md:pb-60">
        <div className="max-w-6xl mx-auto">
          {/* Flashcard - Centered on mobile */}
            <div ref={flashcardRef} className="mb-6 scroll-mt-24 md:scroll-mt-28 flex justify-center items-center min-h-[70vh] md:min-h-0">
             <div className="w-full max-w-lg md:max-w-none">
               {currentCard ? (
                 <FlashcardComponent
                   card={currentCard}
                   isFlipped={isFlipped}
                   onFlip={handleFlipCard}
                   showHint={showHint && studyMode.showHints}
                   onToggleHint={handleToggleHint}
                 />
               ) : (
                 <div className="w-full h-[400px] sm:h-[450px] md:h-[500px] flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-800 dark:to-slate-700 rounded-2xl border-2 border-dashed border-gray-300 dark:border-slate-600 shadow-lg">
                   <div className="text-center space-y-4">
                     <div className="text-gray-500 dark:text-gray-400 text-lg font-medium">Kart yükleniyor...</div>
                     <LoadingSpinner size="lg" />
                   </div>
                 </div>
               )}
             </div>
           </div>
          

          
          {/* Controls - Fixed position for better visibility */}
          <div className={`fixed bottom-0 left-0 right-0 p-4 z-50 transition-all duration-300 ${sidebarOpen ? 'lg:left-64' : ''}`}>
            <div className="max-w-6xl mx-auto flex flex-col gap-3 bg-transparent">
              {/* Top row: Progress (left on desktop, above on mobile) + Finish button */}
              <div className="w-full flex flex-col md:flex-row md:items-center gap-3">
                 {/* Minimal progress bar with counters */}
                 <div className="w-full md:flex-1">
                   <div className="flex items-center justify-between mb-1">
                     <span className="text-xs font-medium text-slate-600 dark:text-slate-300">
                       {currentCardIndex + 1} / {totalCards}
                     </span>
                     <span className="text-xs text-slate-500 dark:text-slate-400">
                       %{Math.round(((currentCardIndex + 1) / totalCards) * 100)}
                     </span>
                   </div>
                   <div className="relative h-2 rounded-full bg-transparent">
                     <div
                       className="h-2 rounded-full bg-gradient-to-r from-indigo-500 via-blue-500 to-cyan-400 shadow-[0_0_12px_-2px_rgba(59,130,246,0.6)] transition-all duration-500"
                       style={{ width: `${Math.round(((currentCardIndex + 1) / totalCards) * 100)}%` }}
                     />
                   </div>
                 </div>
                 
                 {/* Finish button */}
                 <div className="w-full flex justify-center md:justify-end md:w-auto md:flex-none md:ml-4">
                   <Button
                     onClick={handleCompleteSession}
                     disabled={isTransitioning}
                    className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-xs sm:text-sm px-4 py-2 sm:px-6 sm:py-3 font-semibold shadow-xl rounded-xl w-[240px] md:w-auto"
                   >
                     Bitir
                   </Button>
                 </div>
               </div>

              {/* Session Stats - In footer area - Hidden on mobile */}
              <div className="hidden md:grid grid-cols-4 gap-2">
                <Card className="p-2 text-center bg-blue-50/30 dark:bg-blue-900/20 border-blue-200/30 dark:border-blue-700/30 backdrop-blur-sm">
                  <div className="text-sm font-bold text-blue-600 dark:text-blue-400">
                    {sessionStats.cardsStudied || 0}
                  </div>
                  <div className="text-xs font-medium text-blue-700 dark:text-blue-300">Çalışılan</div>
                </Card>
                <Card className="p-2 text-center bg-green-50/30 dark:bg-green-900/20 border-green-200/30 dark:border-green-700/30 backdrop-blur-sm">
                  <div className="text-sm font-bold text-green-600 dark:text-green-400">
                    {sessionStats.cardsCorrect || 0}
                  </div>
                  <div className="text-xs font-medium text-green-700 dark:text-green-300">Doğru</div>
                </Card>
                <Card className="p-2 text-center bg-red-50/30 dark:bg-red-900/20 border-red-200/30 dark:border-red-700/30 backdrop-blur-sm">
                  <div className="text-sm font-bold text-red-600 dark:text-red-400">
                    {sessionStats.cardsIncorrect || 0}
                  </div>
                  <div className="text-xs font-medium text-red-700 dark:text-red-300">Yanlış</div>
                </Card>
                <Card className="p-2 text-center bg-yellow-50/30 dark:bg-yellow-900/20 border-yellow-200/30 dark:border-yellow-700/30 backdrop-blur-sm">
                  <div className="text-sm font-bold text-yellow-600 dark:text-yellow-400">
                    {sessionStats.cardsToReview || 0}
                  </div>
                  <div className="text-xs font-medium text-yellow-700 dark:text-yellow-300">Tekrar</div>
                </Card>
              </div>
              {/* Navigation */}
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Button
                  variant="outline"
                  onClick={handlePrevCard}
                  disabled={isTransitioning || currentCardIndex === 0}
                  className="bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-600 border-slate-300 dark:border-slate-500 text-slate-700 dark:text-slate-200 hover:from-slate-200 hover:to-slate-300 dark:hover:from-slate-600 dark:hover:to-slate-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition-all duration-200 rounded-xl px-4 py-2 font-medium text-sm"
                >
                  <ArrowLeft size={16} className="sm:mr-1" />
                  <span className="hidden sm:inline">Önceki</span>
                </Button>
                

                
                <Button
                  variant="outline"
                  onClick={handleFlipCard}
                  disabled={isTransitioning}
                  className="bg-gradient-to-r from-purple-100 to-pink-200 dark:from-purple-800 dark:to-pink-700 border-purple-300 dark:border-purple-600 text-purple-700 dark:text-purple-200 hover:from-purple-200 hover:to-pink-300 dark:hover:from-purple-700 dark:hover:to-pink-600 shadow-lg transition-all duration-200 rounded-xl px-4 py-2 font-medium text-sm"
                >
                  {isFlipped ? <EyeOff size={16} /> : <Eye size={16} />}
                  <span className="ml-1 hidden sm:inline">
                    {isFlipped ? 'Soruyu Göster' : 'Cevabı Göster'}
                  </span>
                  <span className="ml-1 sm:hidden">
                    {isFlipped ? 'Soru' : 'Cevap'}
                  </span>
                </Button>
                
                <Button
                  variant="outline"
                  onClick={handleNextCard}
                  disabled={isTransitioning || currentCardIndex === totalCards - 1}
                  className="bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-600 border-slate-300 dark:border-slate-500 text-slate-700 dark:text-slate-200 hover:from-slate-200 hover:to-slate-300 dark:hover:from-slate-600 dark:hover:to-slate-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition-all duration-200 rounded-xl px-4 py-2 font-medium text-sm"
                >
                  <span className="hidden sm:inline">Sonraki</span>
                  <ArrowRight size={16} className="sm:ml-1" />
                </Button>
              </div>
              
              {/* Response Buttons - Only show when card is flipped */}
              {isFlipped && (
                <div className="flex flex-row items-center justify-center gap-2 w-full">
                  <Button
                    onClick={() => handleCardResponse('incorrect')}
                    disabled={isTransitioning}
                    className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 dark:from-red-600 dark:to-red-700 dark:hover:from-red-700 dark:hover:to-red-800 text-white text-xs sm:text-sm px-3 sm:px-6 py-2 sm:py-3 flex-1 font-semibold shadow-xl transition-all duration-300 rounded-xl"
                  >
                    Bilmiyorum
                  </Button>
                  
                  <Button
                    onClick={() => handleCardResponse('review')}
                    disabled={isTransitioning}
                    className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 dark:from-yellow-600 dark:to-orange-600 dark:hover:from-yellow-700 dark:hover:to-orange-700 text-white text-xs sm:text-sm px-3 sm:px-6 py-2 sm:py-3 flex-1 font-semibold shadow-xl transition-all duration-300 rounded-xl"
                  >
                    Tekrar Et
                  </Button>
                  
                  <Button
                    onClick={() => handleCardResponse('correct')}
                    disabled={isTransitioning}
                    className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 dark:from-green-600 dark:to-emerald-700 dark:hover:from-green-700 dark:hover:to-emerald-800 text-white text-xs sm:text-sm px-3 sm:px-6 py-2 sm:py-3 flex-1 font-semibold shadow-xl transition-all duration-300 rounded-xl"
                  >
                    Biliyorum
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FlashcardStudy